import {useCallback, useLayoutEffect, useReducer, useRef} from "react";
import styled from "styled-components";
import ResizeObserver from "resize-observer-polyfill";

function nextPowerOf2(i) {
    let j = 0;
    while (i > 0) {
        j = i & -i;
        i -= j;
    }
    return j << 1;
}

class FenwickTree {
    constructor(n) {
        // internally, indexes start at 1
        this.n = n + 1;
        this.n2 = nextPowerOf2(n + 1);
        this.b = Array(n + 1).fill(0);
    }
    add(i, val) {
        const {b,n} = this;
        i += 1;
        b[i] += val;
        i += i & -i;
        while (i <= n) {
            b[i] += val;
            i += i & -i;
        }
    }
    get(i) {
        const {b} = this;
        i += 1;
        let a = b[i];
        i -= i & -i;
        while (i > 0) {
            a += b[i];
            i -= i & -i;
        }
        return a;
    }
    set(i, val) {
        this.add(i, val - this.sum(i, i));
    }
    sum(i, j) {
        return this.get(j) - this.get(i - 1);
    }
    search(val) {
        const {n, n2, b} = this;
        let i = 0;
        let j = n2;
        while (j > 0) {
            if (i + j < n && b[i + j] <= val) {
                val -= b[i + j];
                i += j;
            }
            j >>= 1;
        }
        return i;
    }
    load(a) {
        const {n, b} = this;
        for (let i = 1; i < n; i++) {
            b[i] += a[i - 1];
            const j = i + (i & -i);
            if (j < n) {
                b[j] += b[i];
            }
        }
        return this;
    }
    save() {
        const {n, b} = this;
        const a = Array(n);
        for (let i = n - 1; i > 0; i--) {
            a[i - 1] = b[i];
            const j = i + (i & -i);
            if (j < n) {
                a[j] -= a[i];
            }
        }
        return a;
    }
}

const Container = styled.div`
  position: relative;
  -webkit-overflow-scrolling: touch;
  > * {
    position: absolute;
    left: 0;
    right: 0;
  }
`;

const defaultOptions = { defaultHeight: 250, overscanAbove: 2000, overscanBelow: 2000 };

class State {
    constructor(options) {
        options = {...defaultOptions, ...options};
        this.defaultHeight = options.defaultHeight;
        this.overscanAbove = options.overscanAbove;
        this.overscanBelow = options.overscanBelow;
        this.indexByKey = new Map();
        this.indexByElement = new WeakMap();
        this.container = null;
        this.entries = null;
        this.observer = null;
        this.tree = null;
        this.topItemIndex = 0;
        this.topItemBottom = 0;
        this.flush = () => {};
    }
    setContainer(container) {
        this.container = container;
        this.observer?.disconnect();
        if (!container) {
            this.observer = null;
            return;
        }
        this.observer = new ResizeObserver((resizedEntries) => {
            const { indexByElement, entries, tree } = this;
            for (const resize of resizedEntries) {
                const index = indexByElement.get(resize.target);
                const entry = entries[index];
                if (!entry) { // XXX
                    continue;
                }
                let height;
                if (resize.borderBoxSize) {
                    if (Array.isArray(resize.borderBoxSize)) {
                        // Chrome
                        height = resize.borderBoxSize[0].blockSize;
                    } else {
                        // Firefox
                        height = resize.borderBoxSize.blockSize;
                    }
                } else {
                    // Safari
                    height = resize.contentRect.height;
                }
                entry.height = height;
                tree.set(entry.index, height);
            }
            this.apply();
        });
        for (const {element} of this.entries || []) {
            if (element) {
                this.observer.observe(element);
            }
        }
    }
    apply() {
        return new Promise((resolve, reject) => {
            window.requestAnimationFrame(() => {
                if (!this.container || document.body.style.position === 'fixed') {
                    resolve();
                    return;
                }
                try {
                    const {indexByElement, tree} = this;
                    if (this.topItemIndex > 0) {
                        // compensate any shift in elements
                        const adjustedTopItemBottom = tree.get(this.topItemIndex);
                        const y = adjustedTopItemBottom - this.topItemBottom;
                        if (Math.abs(y) >= 0.1) {
                            window.scrollBy(0, y);
                        }
                    }
                    // update visible window
                    const rect = this.container.getBoundingClientRect();
                    const element = document.scrollingElement;
                    this.visibleTop = Math.max(0, -rect.top);
                    this.visibleHeight = element.clientHeight - rect.top; // container top to bottom of screen
                    this.topItemIndex = tree.search(this.visibleTop);
                    this.topItemBottom = tree.get(this.topItemIndex);
                    // reposition elements
                    for (const element of this.container.children) {
                        const index = indexByElement.get(element);
                        if (typeof index === 'number') {
                            const top = tree.get(index - 1);
                            element.style.transform = `translateY(${top}px)`;
                        }
                    }
                    resolve();
                    if (this.dirty) {
                        this.dirty = false;
                        this.flush();
                    }
                } catch (error) {
                    reject(error);
                }
            });
        });
    }
    updateItems(items, keyOfItem) {
        const map = new Map();
        if (this.entries) {
            for (const entry of this.entries) {
                map.set(entry.key, entry);
            }
        }
        const entries = Array(items.length);
        const indexByKey = new Map();
        const indexByElement = new WeakMap();
        for (let index = 0; index < items.length; index++) {
            const key = keyOfItem(items[index]);
            indexByKey.set(key, index);
            const entry = map.get(key) || this.createEntry(key, this.defaultHeight);
            if (entry.element) {
                indexByElement.set(entry.element, index);
            }
            entry.index = index;
            entries[index] = entry;
        }
        this.entries = entries;
        this.indexByKey = indexByKey;
        this.indexByElement = indexByElement;
        this.tree = new FenwickTree(items.length).load(entries.map(entry => entry.height));
    }
    createEntry(key, height) {
        const entry = { key, height };
        entry.ref = (element) => {
            if (entry.element !== element) {
                const { indexByElement, observer } = this;
                if (entry.element) {
                    indexByElement.delete(element);
                    observer?.unobserve(entry.element);
                }
                entry.element = element;
                if (element) {
                    indexByElement.set(element, entry.index);
                    const {height} = element.getBoundingClientRect();
                    entry.mounted = true;
                    entry.height = height;
                    this.tree.set(entry.index, entry.height);
                    observer?.observe(element);
                    this.dirty = true;
                }
            }
        }
        return entry;
    }
    getRange(pinnedKeys=[]) {
        const { entries, tree, visibleTop, visibleHeight } = this;
        if (!tree) {
            return [];
        }
        const pinnedIndexes = (
            pinnedKeys
                .map((key) => this.indexByKey.get(key))
                .filter(index => index !== undefined)
                .sort()
        );
        const firstIndex = Math.max(0, tree.search(visibleTop - this.overscanAbove) - 1);
        const lastIndex = Math.min(this.entries.length - 1, tree.search(visibleHeight + this.overscanBelow));
        const items = [];
        const addItem = (index) => {
            const { key, ref, mounted } = entries[index];
            const y = this.tree.get(index - 1);
            const style = {transform: `translateY(${y}px)`};
            if (!mounted) {
                style.visibility = 'hidden';
            }
            items.push({ index, key, ref, style });
        };
        for (const index of pinnedIndexes) {
            if (index >= firstIndex) {
                break;
            }
            addItem(index);
        }
        for (let index = firstIndex; index <= lastIndex; index++) {
            addItem(index);
        }
        for (const index of pinnedIndexes) {
            if (index <= lastIndex) {
                continue
            }
            addItem(index);
        }
        return items;
    }
    getHeight() {
        return this.tree?.get(this.entries.length - 1) || 0;
    }
    save() {
        const { topItemIndex, topItemBottom } = this;
        const entries = this.entries.filter((p) => p.mounted).map(({ key, height }) => ({ key, height }));
        return { entries, topItemIndex, topItemBottom };
    }
    load({ entries, topItemIndex, topItemBottom }) {
        this.entries = entries.map(({ key, height }) => this.createEntry(key, height));
        this.topItemIndex = topItemIndex;
        this.topItemBottom = topItemBottom;
    }
}

class LruCache {
    constructor(size) {
        this.size = size;
        this.write = size - 1;
        this.clear();
    }
    put(key, value) {
        if (!this.map.has(key)) {
            this.write = (this.write + 1) % this.size;
            this.map.delete(this.entries[this.write]);
            this.entries[this.write] = key;
        }
        this.map.set(key, value);
    }
    get(key) {
        return this.map.get(key);
    }
    clear() {
        this.map = new Map();
        this.entries = new Array(this.size).fill(null);
    }
}

const cache = new LruCache(10);
if (import.meta.env.DEV) {
    window.cache = cache;
}

function Virtualize({ className, id, items, keyOfItem, renderItem, footer, options, pinnedKeys }) {

    // Initialize virtualizer state.
    const idRef = useRef(null);
    const topRef = useRef(undefined);
    const stateRef = useRef(null);
    if (id !== idRef.current) {
        idRef.current = id;
        stateRef.current = new State(options);
        if (import.meta.env.DEV) {
            window.v = stateRef.current;
        }
        const initState = cache.get(id);
        if (initState) {
            topRef.current = initState.scrollTop;
            stateRef.current.load(initState);
        }
    }

    // Scroll on mount, save state on unmount.
    useLayoutEffect(() => {
        const state = stateRef.current;
        if (topRef.current === undefined) {
            topRef.current = document.scrollingElement.scrollTop;
        } else {
            document.scrollingElement.scrollTop = topRef.current;
        }
        return () => {
            const {scrollTop} = document.scrollingElement;
            cache.put(id, {...state.save(), scrollTop});
        };
    }, [id])

    // Attach virtualizer to the container.
    const containerRef = useRef(null);
    const setContainer = useCallback((container) => {
        if (container !== containerRef.current) {
            containerRef.current = container;
            stateRef.current.setContainer(container);
        }
    }, [id]);

    // A callback to apply any needed changes to the DOM.
    const [, rerender] = useReducer((i, _) => i + 1, 0);
    const apply = useCallback(() => {
        if (containerRef.current && document.body.style.position !== 'fixed') {
            stateRef.current.apply(containerRef.current).then(rerender);
        }
    }, []);

    // Apply changes on scroll, and update 'flush' callback.
    useLayoutEffect(() => {
        if (stateRef.current) {
            stateRef.current.flush = apply;
        }
        window.addEventListener('scroll', apply);
        return () => window.removeEventListener('scroll', apply);
    }, []);

    // Update items when they change; keyOfItem is assumed constant.
    const keyOfItemRef = useRef(keyOfItem);
    useLayoutEffect(() => {
        stateRef.current.updateItems(items, keyOfItemRef.current);
        apply();
    }, [items]);

    const height = stateRef.current.getHeight() || '100vh';
    return (
        <Container className={className} ref={setContainer} style={{minHeight: height}}>
            {stateRef.current.getRange(pinnedKeys).map(({ key, index, ref, style }) => (
                <div key={key} id={key} ref={ref} style={style}>
                    {items[index] && renderItem(items[index], index)}
                </div>
            ))}
            {footer && (
                <div style={{transform: `translateY(${height}px)`}}>{footer}</div>
            )}
        </Container>
    );
}

export default Virtualize;
